import React, { forwardRef, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

const VodPlayer = forwardRef(({ file, showControls = true, autoPlay = false }, ref) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);

  useEffect(() => {
    if (!file) return;

    const initPlayer = () => {
      if (playerRef.current) {
        playerRef.current.dispose();
      }

      const videoElement = videoRef.current;
      if (!videoElement) return;

      playerRef.current = videojs(videoElement, {
        controls: showControls,
        autoplay: autoPlay,
        preload: 'auto',
        sources: [{
          src: `/vod/${encodeURIComponent(file)}`,
          type: 'video/mp4'
        }],
        fluid: true,
        playbackRates: [0.5, 1, 1.5, 2],
        userActions: {
          hotkeys: true
        }
      });

      // Expose player methods to parent
      if (ref) {
        ref.current = {
          play: () => playerRef.current.play(),
          pause: () => playerRef.current.pause(),
          currentTime: playerRef.current.currentTime,
          duration: playerRef.current.duration,
          seekTo: (time) => { playerRef.current.currentTime(time); }
        };
      }

      setPlayerReady(true);

      // Error handling
      playerRef.current.on('error', (e) => {
        console.error('VOD Player error:', e);
      });
    };

    initPlayer();

    // Cleanup
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [file, showControls, autoPlay, ref]);

  return (
    <div className="relative w-full h-full">
      <div data-vjs-player className="w-full h-full">
        <video
          ref={videoRef}
          className="video-js vjs-default-skin vjs-big-play-centered"
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      {!playerReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <div className="text-gray-400">Loading video...</div>
        </div>
      )}
    </div>
  );
});

VodPlayer.displayName = 'VodPlayer';
export default VodPlayer;
